import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  TextField, 
  Grid, 
  Divider, 
  CircularProgress,
  Box
} from '@mui/material';
import Alert from '@mui/lab/Alert';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { db, auth } from '../firebase';
import { 
  doc, 
  collection, 
  addDoc, 
  setDoc, 
  serverTimestamp, 
  query, 
  where, 
  getDocs,
  getDoc
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword, 
  updateProfile, 
  fetchSignInMethodsForEmail 
} from 'firebase/auth';
import PaymentForm from '../components/subscriptions/PaymentForm';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(3),
}));

const SubscriptionDetails = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.5rem',
  marginTop: theme.spacing(1),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 0),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
}));

const FormField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const BenefitItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const CheckIcon = styled('span')(({ theme }) => ({
  color: theme.palette.success.main,
  marginRight: theme.spacing(1),
}));

const OrgInfo = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const OrgLogo = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '150px',
  height: 'auto',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

// Steps for the subscription purchase flow
const steps = ['Account Setup', 'Payment Information', 'Review & Confirm'];

type RouteParams = {
  subscriptionId?: string;
  organizationId?: string;
  planId?: string;
};

const SubscriptionPurchasePage: React.FC = () => {
  const params = useParams<RouteParams>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const subscriptionId = params.subscriptionId || '';
  const organizationId = params.organizationId;
  const planId = params.planId;
  
  // State variables
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [organization, setOrganization] = useState<any>(null);
  
  // Form data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<any>(null);
  
  // Fetch subscription details
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        setLoading(true);
        
        let orgId = '';
        let subPlanId = '';
        
        // Handle both URL formats
        if (subscriptionId) {
          // Parse the subscription ID to get org ID and plan ID
          const parts = subscriptionId.split('_');
          if (parts.length === 2) {
            [orgId, subPlanId] = parts;
          } else {
            setError('Invalid subscription link');
            setLoading(false);
            return;
          }
        } else if (organizationId && planId) {
          // Use the direct organizationId and planId from URL parameters
          orgId = organizationId;
          subPlanId = planId;
        } else {
          setError('Invalid subscription link');
          setLoading(false);
          return;
        }
        
        // Get organization data
        const orgDocRef = doc(db, 'organizations', orgId);
        const orgDocSnap = await getDoc(orgDocRef);
        
        if (!orgDocSnap.exists()) {
          setError('Organization not found');
          setLoading(false);
          return;
        }
        
        const orgData = { id: orgDocSnap.id, ...orgDocSnap.data() };
        setOrganization(orgData);
        
        // Get subscription plan data
        const planDocRef = doc(db, 'organizations', orgId, 'subscriptionPlans', subPlanId);
        const planDocSnap = await getDoc(planDocRef);
          
        if (!planDocSnap.exists()) {
          setError('Subscription plan not found');
          setLoading(false);
          return;
        }
        
        const planData = { id: planDocSnap.id, ...planDocSnap.data() };
        setSubscriptionData({
          organizationId: orgId,
          planId: subPlanId,
          ...planData
        });
        
        // Pre-fill email if user is logged in
        if (currentUser) {
          setEmail(currentUser.email || '');
          setActiveStep(1); // Skip account creation if already logged in
        }
        
      } catch (err) {
        console.error('Error fetching subscription details:', err);
        setError('Failed to load subscription details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscriptionDetails();
  }, [subscriptionId, organizationId, planId, currentUser]);
  
  // Handle next step
  const handleNext = async () => {
    if (activeStep === 0) {
      // Account creation step
      if (!email || !password || !firstName || !lastName) {
        setError('Please fill in all required fields');
        return;
      }
      
      try {
        setLoading(true);
        
        // Check if the user already exists but is not logged in
        const methods = await fetchSignInMethodsForEmail(auth, email);
        
        if (methods.length > 0) {
          setError('An account with this email already exists. Please login first.');
          setLoading(false);
          return;
        }
        
        // Create a new user account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Update user profile
        if (userCredential.user) {
          await updateProfile(userCredential.user, {
            displayName: `${firstName} ${lastName}`
          });
          
          // Create user document in Firestore
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email,
            firstName,
            lastName,
            displayName: `${firstName} ${lastName}`,
            createdAt: serverTimestamp(),
            isOnboarded: false
          });
        }
        
        setLoading(false);
        setActiveStep(prevStep => prevStep + 1);
        
      } catch (err: any) {
        console.error('Error creating account:', err);
        setError(err.message || 'Failed to create account');
        setLoading(false);
      }
    } else if (activeStep === 1) {
      // Payment information step
      if (!paymentMethod) {
        setError('Please add a valid payment method');
        return;
      }
      
      setActiveStep(prevStep => prevStep + 1);
    } else if (activeStep === 2) {
      // Confirmation step
      try {
        setLoading(true);
        
        if (!currentUser || !subscriptionData) {
          setError('You must be logged in to complete this purchase');
          setLoading(false);
          return;
        }
        
        // Process the payment and create subscription
        const userId = currentUser.uid;
        
        // Create a new subscription document
        const userSubscriptionsRef = collection(db, 'users', userId, 'subscriptions');
        const subscriptionRef = await addDoc(userSubscriptionsRef, {
          organizationId: subscriptionData.organizationId,
          planId: subscriptionData.planId,
          planName: subscriptionData.name,
          price: subscriptionData.price,
          billingCycle: subscriptionData.billingCycle,
          status: 'active',
          paymentMethodId: paymentMethod.id,
          startDate: serverTimestamp(),
          endDate: calculateEndDate(subscriptionData.billingCycle),
          createdAt: serverTimestamp()
        });
          
        // Add the user to the organization's members
        const memberRef = doc(db, 'organizations', subscriptionData.organizationId, 'members', userId);
        await setDoc(memberRef, {
          userId,
          role: 'subscriber',
          planId: subscriptionData.planId,
          subscriptionId: subscriptionRef.id,
          joinedAt: serverTimestamp()
        });
          
        // Redirect to success page
        navigate('/dashboard');
        
      } catch (err: any) {
        console.error('Error processing subscription:', err);
        setError('Failed to process your subscription. Please try again later.');
        setLoading(false);
      }
    }
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  // Calculate subscription end date based on billing cycle
  const calculateEndDate = (billingCycle: string) => {
    const date = new Date();
    
    switch (billingCycle) {
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'annually':
        date.setFullYear(date.getFullYear() + 1);
        break;
      default:
        date.setMonth(date.getMonth() + 1);
    }
    
    return date;
  };
  
  // Handle payment method selection
  const handlePaymentMethodSelected = (method: any) => {
    setPaymentMethod(method);
    setError('');
  };
  
  // Render account setup step
  const renderAccountSetup = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Create Your Account</Typography>
        <Typography variant="body2" color="text.secondary">
          Create an account to complete your subscription
        </Typography>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormField
          label="First Name"
          value={firstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
          fullWidth
          required
        />
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <FormField
          label="Last Name"
          value={lastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
          fullWidth
          required
        />
      </Grid>
      
      <Grid item xs={12}>
        <FormField
          label="Email Address"
          type="email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          fullWidth
          required
        />
      </Grid>
      
      <Grid item xs={12}>
        <FormField
          label="Password"
          type="password"
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          fullWidth
          required
          helperText="Must be at least 8 characters"
        />
      </Grid>
    </Grid>
  );
  
  // Render payment information step
  const renderPaymentInformation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Payment Information</Typography>
        <Typography variant="body2" color="text.secondary">
          Add your payment details to complete the subscription
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <PaymentForm onPaymentMethodSelected={handlePaymentMethodSelected} />
      </Grid>
    </Grid>
  );
  
  // Render confirmation step
  const renderConfirmation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6">Review Your Subscription</Typography>
        <Typography variant="body2" color="text.secondary">
          Please review your subscription details before confirming
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <StyledPaper variant="outlined">
          <Typography variant="subtitle1">
            <strong>Organization:</strong> {organization?.name}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Plan:</strong> {subscriptionData?.name}
          </Typography>
          <StyledDivider />
          <Typography variant="subtitle1">
            <strong>Billing:</strong> ${subscriptionData?.price} / {subscriptionData?.billingCycle}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Your subscription will automatically renew at the end of each billing period.
          </Typography>
        </StyledPaper>
      </Grid>
    </Grid>
  );
  
  // Render step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderAccountSetup();
      case 1:
        return renderPaymentInformation();
      case 2:
        return renderConfirmation();
      default:
        return 'Unknown step';
    }
  };
  
  // Render loading state
  if (loading && !subscriptionData) {
    return (
      <Container sx={{ maxWidth: '800px', margin: '0 auto', padding: 4 }}>
        <LoadingContainer>
          <CircularProgress />
          <Typography variant="body1" sx={{ marginTop: 2 }}>
            Loading subscription details...
          </Typography>
        </LoadingContainer>
      </Container>
    );
  }
  
  // Render error state
  if (error && !subscriptionData) {
    return (
       <Container sx={{ maxWidth: '800px', margin: '0 auto', padding: 4 }}>
          <StyledPaper>
             <Alert severity='error'>{error}</Alert>
             <Box mt={2} display='flex' justifyContent='center'>
                <Button variant='contained' color='primary' onClick={() => navigate('/dashboard')}>
                   Return to Home
                </Button>
             </Box>
          </StyledPaper>
       </Container>
    );
  }

  return (
    <Container sx={{ maxWidth: '800px', margin: '0 auto', padding: 4 }}>
      <StyledPaper>
        {/* Organization and Subscription Info */}
        {organization && subscriptionData && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <OrgInfo>
                {organization.logoUrl && (
                  <OrgLogo 
                    src={organization.logoUrl} 
                    alt={organization.name}
                  />
                )}
                <Typography variant="h6">{organization.name}</Typography>
              </OrgInfo>
            </Grid>
            
            <Grid item xs={12} sm={8}>
              <Typography variant="h5">{subscriptionData.name}</Typography>
              <Typography variant="body1" color="text.secondary">
                {subscriptionData.description}
              </Typography>
              <PriceText>
                ${subscriptionData.price} / {subscriptionData.billingCycle}
              </PriceText>
              
              {subscriptionData.benefits && subscriptionData.benefits.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2">
                    <strong>Benefits include:</strong>
                  </Typography>
                  {subscriptionData.benefits.map((benefit: string, index: number) => (
                    <BenefitItem key={index}>
                      <CheckIcon>✓</CheckIcon>
                      <Typography variant="body2">{benefit}</Typography>
                    </BenefitItem>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        )}
        
        <StyledDivider />
        
        {/* Stepper */}
        <StyledStepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>
        
        {/* Error messages */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {/* Step content */}
        {getStepContent(activeStep)}
        
        {/* Navigation buttons */}
        <ButtonContainer>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={loading}
          >
            {activeStep === steps.length - 1 ? (
              loading ? <CircularProgress size={24} /> : 'Subscribe'
            ) : (
              'Next'
            )}
          </Button>
        </ButtonContainer>
      </StyledPaper>
    </Container>
  );
};

export default SubscriptionPurchasePage;
